import { useCallback, useEffect, useRef, useState } from 'react';

// THE CLOCK BEHIND EVERY GUIDE ANIMATION (Oct 2026).
//
// `t` runs from 0 to 1 over `duration` ms. It plays by itself the first time
// the card scrolls into view, then rests at 1, fully drawn. replay() plays it
// again. Before it plays, and for readers who ask for reduced motion, t is 1,
// so the pre-rendered page and a still screen show the whole story.
//
//   const story = useStory(20000);
//   <Explainer story={story} ...>{draw(story.t)}</Explainer>

export default function useStory(duration = 20000) {
  const ref = useRef(null);
  const [t, setT] = useState(1);
  const [playing, setPlaying] = useState(false);
  const reduce = useRef(typeof window !== 'undefined' && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  useEffect(() => {
    const el = ref.current;
    if (!el || reduce.current || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((es) => {
      if (es.some((e) => e.isIntersecting)) { setT(0); setPlaying(true); io.disconnect(); }
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!playing) return undefined;
    let raf;
    let start = null;
    const tick = (now) => {
      if (start == null) start = now;
      const x = Math.min(1, (now - start) / duration);
      setT(x);
      if (x < 1) raf = requestAnimationFrame(tick); else setPlaying(false);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, duration]);

  const replay = useCallback(() => {
    if (reduce.current) { setT(1); return; }
    setT(0);
    setPlaying(false);
    requestAnimationFrame(() => setPlaying(true));
  }, []);

  return { ref, t, playing, replay };
}

// Helpers for drawing: where `t` is inside a stretch [a, b], 0..1.
export const span = (t, a, b) => Math.max(0, Math.min(1, (t - a) / (b - a)));
export const ease = (x) => 1 - (1 - x) * (1 - x);
